import type { CornerRadiusModifierValue } from "schemas/generated/fileModifiers";

import React, { useCallback, useEffect, useMemo, useState } from "react";

import AllCorners from "@svg/modifiers/direction/all-corners";
import BottomLeftCorner from "@svg/modifiers/direction/bottom-left-corner";
import BottomRightCorner from "@svg/modifiers/direction/bottom-right-corner";
import TopLeftCorner from "@svg/modifiers/direction/top-left-corner";
import TopRightCorner from "@svg/modifiers/direction/top-right-corner";

import { LengthInputSelector } from "@v2/features/modifiers/components/LengthInputSelector";
import { useOptimisticModifierValue } from "@v2/features/modifiers/hooks/useOptimisticModifierValue";
import { ChevronDown } from "lucide-react";
import { MAX_CORNER_RADIUS } from "replo-runtime/shared/constants";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { parseUnit } from "replo-runtime/shared/utils/units";

type RadiusMode = "unified" | "individual";

const clampRadius = (value: number): number => {
  return Math.min(Math.max(value, 0), MAX_CORNER_RADIUS);
};

const normalizeRadiusValue = (value: number): CornerRadiusValue => {
  if (value >= MAX_CORNER_RADIUS) {
    return "full";
  }
  return value;
};

const CORNER_RADIUS_OPTIONS = [
  {
    value: 0,
    label: "None",
  },
  {
    value: 4,
    label: "Small",
  },
  {
    value: 8,
    label: "Medium",
  },
  {
    value: 12,
    label: "Large",
  },
  {
    value: 16,
    label: "Extra Large",
  },
  {
    value: 24,
    label: "2X Large",
  },
  {
    value: 40,
    label: "3X Large",
  },
  {
    value: 72,
    label: "4X Large",
  },
  {
    value: "full",
    label: "Full",
  },
] as const;

type CornerRadiusValue = number | "full";

interface RadiusValue {
  topLeft?: CornerRadiusValue;
  topRight?: CornerRadiusValue;
  bottomRight?: CornerRadiusValue;
  bottomLeft?: CornerRadiusValue;
}

interface CornerRadiusModifierProps {
  onChange: (modifiers: CornerRadiusModifierValue[]) => void;
  onModeChange?: (isIndividual: boolean) => void;
  showButton?: boolean;
  mode?: "unified" | "individual";
  showIndividualInputs?: boolean;
  onlyShowIndividualInputs?: boolean;
}

const CornerRadiusModifier: React.FC<CornerRadiusModifierProps> = ({
  onChange,
  onModeChange,
  mode: externalMode,
  showIndividualInputs = false,
  onlyShowIndividualInputs = false,
}) => {
  const topLeft =
    useOptimisticModifierValue({
      modifierType: { type: "cornerRadius", corner: "topLeft", value: 0 },
      extractor: (elementDesignation) => {
        return (
          elementDesignation.element.styleOverrides?.cornerTopLeftRadius ?? 0
        );
      },
    }) ?? 0;

  const topRight =
    useOptimisticModifierValue({
      modifierType: { type: "cornerRadius", corner: "topRight", value: 0 },
      extractor: (elementDesignation) => {
        return (
          elementDesignation.element.styleOverrides?.cornerTopRightRadius ?? 0
        );
      },
    }) ?? 0;

  const bottomRight =
    useOptimisticModifierValue({
      modifierType: { type: "cornerRadius", corner: "bottomRight", value: 0 },
      extractor: (elementDesignation) => {
        return (
          elementDesignation.element.styleOverrides?.cornerBottomRightRadius ??
          0
        );
      },
    }) ?? 0;

  const bottomLeft =
    useOptimisticModifierValue({
      modifierType: { type: "cornerRadius", corner: "bottomLeft", value: 0 },
      extractor: (elementDesignation) => {
        return (
          elementDesignation.element.styleOverrides?.cornerBottomLeftRadius ?? 0
        );
      },
    }) ?? 0;

  const extractedRadius = useMemo(() => {
    return {
      topLeft: normalizeRadiusValue(topLeft),
      topRight: normalizeRadiusValue(topRight),
      bottomRight: normalizeRadiusValue(bottomRight),
      bottomLeft: normalizeRadiusValue(bottomLeft),
    };
  }, [topLeft, topRight, bottomRight, bottomLeft]);

  const [radiusValue, setRadiusValue] =
    useOverridableState<RadiusValue>(extractedRadius);

  const [internalRadiusMode] = useState<RadiusMode>(() => {
    const { topLeft, topRight, bottomRight, bottomLeft } = extractedRadius;
    if (
      topLeft === topRight &&
      topLeft === bottomRight &&
      topLeft === bottomLeft
    ) {
      return "unified";
    }
    return "individual";
  });

  const radiusMode = externalMode ?? internalRadiusMode;

  useEffect(() => {
    onModeChange?.(radiusMode === "individual");
  }, [radiusMode, onModeChange]);

  const handleRadiusChange = useCallback(
    (radius: Partial<RadiusValue>) => {
      const newRadius = { ...radiusValue, ...radius };
      if (radiusMode === "unified") {
        const unifiedValue = newRadius.topLeft;
        newRadius.topRight = unifiedValue;
        newRadius.bottomRight = unifiedValue;
        newRadius.bottomLeft = unifiedValue;
      }
      setRadiusValue(newRadius);

      const modifiers: CornerRadiusModifierValue[] = [];

      const getCornerValue = (value?: CornerRadiusValue): number | "full" => {
        if (value === "full") {
          return "full";
        }
        return value ?? 0;
      };

      // Note (Kevin, 12/3/2025): Always include all corners so the optimistic queue
      // has complete state, sometimes the optimistic queue would flash to "None" on unchanged corners, this prevents that.
      modifiers.push(
        {
          type: "cornerRadius",
          corner: "topLeft",
          value: getCornerValue(newRadius.topLeft),
        },
        {
          type: "cornerRadius",
          corner: "topRight",
          value: getCornerValue(newRadius.topRight),
        },
        {
          type: "cornerRadius",
          corner: "bottomRight",
          value: getCornerValue(newRadius.bottomRight),
        },
        {
          type: "cornerRadius",
          corner: "bottomLeft",
          value: getCornerValue(newRadius.bottomLeft),
        },
      );

      onChange(modifiers);
    },
    [radiusValue, radiusMode, onChange, setRadiusValue],
  );

  const getIndividualCornerRadiusDisplayValue = (
    value?: CornerRadiusValue,
  ): string => {
    if (value === "full") {
      return "Full";
    }
    if (!value) {
      return "0";
    }
    return clampRadius(value).toString();
  };

  const getUnifiedDisplayValue = (): string => {
    const { topLeft, topRight, bottomRight, bottomLeft } = radiusValue;
    const allCornersEqual =
      topLeft === topRight && topLeft === bottomRight && topLeft === bottomLeft;

    if (!allCornersEqual) {
      return "Mixed";
    }
    // Note (Kevin, 12/2/2025): since we know all corners are equal, we can just check the top left corner
    return getIndividualCornerRadiusDisplayValue(topLeft);
  };

  const parseRadiusValue = (newValue: string): CornerRadiusValue => {
    if (newValue === "full") {
      return "full";
    }
    const result = parseUnit(
      newValue,
      { value: 0, unit: "px" },
      "default",
      "px",
    );
    return Number(result.value) ?? 0;
  };

  const mappedRadiusOptions = CORNER_RADIUS_OPTIONS.map((option) => ({
    ...option,
    value: option.value.toString(),
  }));

  if (onlyShowIndividualInputs) {
    return (
      <div className="grid grid-cols-2 gap-2 items-start">
        {/* Top Left */}
        <LengthInputSelector
          field="topLeftRadius"
          value={getIndividualCornerRadiusDisplayValue(radiusValue.topLeft)}
          onChange={(newValue) => {
            return handleRadiusChange({ topLeft: parseRadiusValue(newValue) });
          }}
          placeholder="0px"
          metrics={["px", "%"]}
          unitDefaults={{ value: "0", unit: "px" }}
          menuOptions={mappedRadiusOptions}
          minValues={{ px: 0, "%": 0 }}
          maxValues={{ px: MAX_CORNER_RADIUS, "%": 100 }}
          inputClassName="w-full"
          startEnhancer={<TopLeftCorner className="w-3 h-3" />}
          endEnhancer={() => <ChevronDown size={12} />}
        />
        {/* Top Right */}
        <LengthInputSelector
          field="topRightRadius"
          value={getIndividualCornerRadiusDisplayValue(radiusValue.topRight)}
          onChange={(newValue) => {
            return handleRadiusChange({ topRight: parseRadiusValue(newValue) });
          }}
          placeholder="0px"
          metrics={["px", "%"]}
          unitDefaults={{ value: "0", unit: "px" }}
          menuOptions={mappedRadiusOptions}
          minValues={{ px: 0, "%": 0 }}
          maxValues={{ px: MAX_CORNER_RADIUS, "%": 100 }}
          inputClassName="w-full"
          startEnhancer={<TopRightCorner className="w-3 h-3" />}
          endEnhancer={() => <ChevronDown size={12} />}
        />
        {/* Bottom Left */}
        <LengthInputSelector
          field="bottomLeftRadius"
          value={getIndividualCornerRadiusDisplayValue(radiusValue.bottomLeft)}
          onChange={(newValue) => {
            return handleRadiusChange({
              bottomLeft: parseRadiusValue(newValue),
            });
          }}
          placeholder="0px"
          metrics={["px", "%"]}
          unitDefaults={{ value: "0", unit: "px" }}
          menuOptions={mappedRadiusOptions}
          minValues={{ px: 0, "%": 0 }}
          maxValues={{ px: MAX_CORNER_RADIUS, "%": 100 }}
          inputClassName="w-full"
          startEnhancer={<BottomLeftCorner className="w-3 h-3" />}
          endEnhancer={() => <ChevronDown size={12} />}
        />
        {/* Bottom Right */}
        <LengthInputSelector
          field="bottomRightRadius"
          value={getIndividualCornerRadiusDisplayValue(radiusValue.bottomRight)}
          onChange={(newValue) => {
            return handleRadiusChange({
              bottomRight: parseRadiusValue(newValue),
            });
          }}
          placeholder="0px"
          metrics={["px", "%"]}
          unitDefaults={{ value: "0", unit: "px" }}
          menuOptions={mappedRadiusOptions}
          minValues={{ px: 0, "%": 0 }}
          maxValues={{ px: MAX_CORNER_RADIUS, "%": 100 }}
          inputClassName="w-full"
          startEnhancer={<BottomRightCorner className="w-3 h-3" />}
          endEnhancer={() => <ChevronDown size={12} />}
        />
      </div>
    );
  }

  const handleUnifiedRadiusChange = (newValue: string) => {
    const parsedValue = parseRadiusValue(newValue);
    if (showIndividualInputs) {
      onModeChange?.(false);
      // Note (Nick, 11/4/2025): Individual mode, so must specify all corners to set them all
      handleRadiusChange({
        topLeft: parsedValue,
        topRight: parsedValue,
        bottomRight: parsedValue,
        bottomLeft: parsedValue,
      });
    } else {
      // Note (Nick, 11/4/2025): Unified mode, so setting topLeft sets it for all corners
      handleRadiusChange({ topLeft: parsedValue });
    }
  };

  return (
    <LengthInputSelector
      field="unifiedRadius"
      value={getUnifiedDisplayValue()}
      onChange={handleUnifiedRadiusChange}
      placeholder="0px"
      metrics={["px", "%"]}
      unitDefaults={{ value: "0", unit: "px" }}
      menuOptions={mappedRadiusOptions}
      minValues={{ px: 0, "%": 0 }}
      maxValues={{ px: MAX_CORNER_RADIUS, "%": 100 }}
      allowedNonUnitValues={["Mixed", "full"]}
      inputClassName="w-full"
      startEnhancer={<AllCorners className="w-3 h-3" />}
      endEnhancer={() => <ChevronDown size={12} />}
    />
  );
};

export default CornerRadiusModifier;
